'use client';

import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from '@/app_components/common/Link';
import { Pagination } from '@/app_components/common/pagination';
import {
  defaultRatesFormValues,
  type RatesApiParameters,
  type RatesTypes,
  RatesWidgetMapper,
  ratesInfoOutput,
} from '@/app_data/widgets/RedVentures/constants';
import type { FilterBy, RatesData } from '@/app_data/widgets/utils';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { useWidgetsIndex } from '@/app_lib/hooks/useWidgetsIndex';
import { getPageViewDataLayerObject, trackEvent } from '@/app_lib/services/Gtm/utils';
import { formatDate } from '@/app_lib/utils/utils';
import { InfoItem } from './InfoItem';
import { RatesCard } from './RatesCard';
import { RatesInput } from './RatesInput';
import { TopSection } from './TopSection';

const getPageCount = (recordCount: number, pageSize: number) => {
  if (recordCount === 0) {
    return 1;
  }
  return Math.ceil(recordCount / pageSize);
};

const paginate = (array: RatesData[], page: number, pageSize: number) => {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return array.slice(start, end);
};

interface RatesWidgetProps {
  className?: string;
  userZipCode?: string; // TODO: Add to the form
  type: RatesTypes;
  initialData?: RatesData[];
  // TODO: Add this back
  // postId: number;
}

const depositAmountDefault = 25000;
const mobilePageSize = 2;
const desktopPageSize = 3;

// TODO: Tracking data
export const RatesWidget = ({ initialData = [], type }: RatesWidgetProps) => {
  const [page, setPage] = useState(1);
  const widgetIndex = useWidgetsIndex();

  const [depositAmount, setDepositAmount] = useState(depositAmountDefault);
  const [rates, setRates] = useState<RatesData[]>(initialData);
  const [fetched, setFetched] = useState(Boolean(initialData.length));

  const mobileQuery = useMediaQuery(Breakpoints.Mobile);

  const pageSize = mobileQuery ? mobilePageSize : desktopPageSize;

  const title = RatesWidgetMapper[type].title;
  const inputs = RatesWidgetMapper[type].inputs;
  const outputs = RatesWidgetMapper[type].outputs;
  const getDataFunc = RatesWidgetMapper[type].getDataFunc;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <only run on mount>
  useEffect(() => {
    async function fetchInitialData() {
      return await getDataFunc(defaultRatesFormValues);
    }

    if (!initialData.length) {
      fetchInitialData().then((data) => {
        setRates(data);
        setFetched(true);
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: helps with development so we don't have to refresh the page
  useEffect(() => {
    setPage(1);
  }, [mobileQuery]);

  useEffect(() => {
    // TODO: Is this outdated? Shouldn't this just be an impression-element with data-content-placement?
    const pageView = getPageViewDataLayerObject();
    trackEvent({
      ...pageView,
      // biome-ignore lint/style/useNamingConvention: <external data>
      contentPlacementCD: 'recommends widget',
      eventAction: 'impression',
      eventCategory: 'recommends widget',
      eventLabel: `${type.toLowerCase()} - ${widgetIndex}`,
    });
  }, [type, widgetIndex]);

  const fetchData = useCallback(
    async ({
      depositAmount: depositInput,
      page: pageInput,
      pageSize: pageSizeInput,
      minTermLength,
      maxTermLength,
      zip,
      accountType,
    }: RatesApiParameters) => {
      try {
        const data = await getDataFunc({
          accountType,
          depositAmount: depositInput,
          maxTermLength,
          minTermLength,
          page: pageInput,
          pageSize: pageSizeInput,
          zip,
        });
        setRates(data.sort((dataA, dataB) => (dataB.is_paid ? 1 : 0) - (dataA.is_paid ? 1 : 0)));
        if (depositInput !== depositAmount) {
          setDepositAmount(depositInput || 0);
        }
      } catch {
        // TODO: Add logging
      } finally {
        setPage(1);
        setFetched(true);
      }
    },
    [depositAmount, getDataFunc],
  );

  const handleOnInputChange = (filters: FilterBy) => {
    fetchData(filters);
  };

  return (
    <section className='not-prose @container flex flex-col pb-4 gap-4 items-start border-1 border-neutrals-300 bg-white'>
      {/* Header */}
      <TopSection title={`${title} ${formatDate(new Date().toString())}`} />

      {/* Form */}
      <RatesInput
        inputs={inputs}
        onChange={handleOnInputChange}
      />

      {/* Bank Cards Container */}
      <div className='flex flex-col items-start gap-4 self-stretch'>
        {/* Data */}
        <div className='hidden @2xl:flex items-start self-stretch justify-between gap-4 py-0 px-4'>
          {outputs.map((output) => {
            const outputItem = ratesInfoOutput[output];
            return (
              <InfoItem
                key={`rates_${output}`}
                side={outputItem.side}
                title={outputItem.title}
                tooltip={outputItem.tooltip?.replace('$DEPOSIT_AMOUNT$', depositAmount.toString())}
              />
            );
          })}
        </div>

        {/* Bank Cards */}
        <ul className='flex flex-col items-start self-stretch'>
          {rates.length === 0 && !fetched
            ? Array.from({ length: pageSize }).map(() => (
                <li
                  className='odd:bg-neutrals-200 w-full h-[250px] animate-pulse'
                  key={uuidv4()}
                />
              ))
            : paginate(rates, page, pageSize).map((cdRate, index) => (
                <li
                  className='odd:bg-neutrals-200 w-full'
                  key={`rates_${cdRate.ep_url}${index}`}
                >
                  <RatesCard
                    // postId={postId}
                    data={cdRate}
                    depositAmount={depositAmount}
                    outputs={outputs}
                  />
                </li>
              ))}
        </ul>
        {rates.length === 0 && fetched && (
          <div className='flex items-center justify-center w-full h-[200px]'>
            <h2 className='typography-body-graphik-xsmall font-graphik-compact text-neutrals-400'>No results found</h2>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        onPageChange={(newPage) => setPage(newPage)}
        totalPages={getPageCount(rates.length, pageSize)}
      />
      <Link
        className='flex self-center items-center gap-2 @3xl:hidden'
        href='https://www.bankrate.com/'
        rel='nofollow noopener noreferrer'
        target='_blank'
      >
        <h2 className='typography-body-graphik-xsmall hover:underline font-graphik-compact text-neutrals-400'>
          Powered by
        </h2>
        <span className='icon w-[60px] icon-bankrate text-black' />
      </Link>
    </section>
  );
};
